import { useEffect, useState, useMemo } from 'react';
import { BarPlot, BarLabelProps } from '@mui/x-charts/BarChart';
import { ChartContainer, ChartsTooltip, ChartsXAxis, ChartsYAxis, useAnimate } from '@mui/x-charts';
import { formatMoney } from '@dill/dill-shared/dist/src';
import { interpolateObject } from '@mui/x-charts-vendor/d3-interpolate';
import { styled } from '@mui/material';
import { useAppSelector } from '../../../../../../redux/store';
import { COLORS } from '../../../../../../utils/colors';

const Text = styled('text')(({ theme }) => ({
  ...theme?.typography?.body2,
  stroke: 'none',
  fill: (theme.vars || theme)?.palette?.text?.primary,
  transition: 'opacity 0.2s ease-in, fill 0.2s ease-in',
  textAnchor: 'middle',
  dominantBaseline: 'central',
  pointerEvents: 'none',
}));

function BarLabel(props: BarLabelProps) {
  const {
    seriesId,
    dataIndex,
    color,
    isFaded,
    isHighlighted,
    classes,
    xOrigin,
    yOrigin,
    x,
    y,
    width,
    height,
    layout,
    skipAnimation,
    ...otherProps
  } = props;

  const animatedProps = useAnimate(
    { x: x + x / 3 + width, y: y + height / 2 },
    {
      initialProps: { x: x + yOrigin, y: yOrigin },
      createInterpolator: interpolateObject,
      transformProps: (p) => p,
      applyProps: (element: SVGTextElement, p) => {
        element.setAttribute('x', p.x.toString());
        element.setAttribute('y', p.y.toString());
      },
      skip: skipAnimation,
    }
  );

  return <Text {...otherProps} fill={color} textAnchor="middle" {...animatedProps} />;
}

function SupplierTopPayingCustomersChart() {
  const { supplierAnnualReport } = useAppSelector((state) => state.reports);
  const [dataSet, setSataSet] = useState<
    {
      totalAmountInTransactions: number;
      customerName: string;
      cleanName: string;
      rank: number;
      percentage: number;
      transactionCount: number;
    }[]
  >();

  const enhancedData = useMemo(() => {
    if (!supplierAnnualReport) return null;

    const customers = supplierAnnualReport.yearSummary.topCustomersOfYear;
    const totalRevenue = customers.reduce((sum, customer) => sum + (customer.totalAmount || 0), 0);

    return customers.map((details, index) => {
      const cleanName =
        details.customer.name.length > 25
          ? `${details.customer.name.substring(0, 25)}...`
          : details.customer.name;

      return {
        totalAmountInTransactions: details.totalAmount || 0,
        customerName: details.customer.name,
        cleanName: cleanName,
        rank: index + 1,
        percentage: totalRevenue > 0 ? ((details.totalAmount || 0) / totalRevenue) * 100 : 0,
        transactionCount: 0,
      };
    });
  }, [supplierAnnualReport]);

  useEffect(() => {
    if (enhancedData) {
      setSataSet(enhancedData);
    }
  }, [enhancedData]);

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
            Top customers on Dill
          </h3>
          <p className="text-sm text-gray-500">Ranked by total transaction volume</p>
        </div>
      </div>
      {dataSet && (
        <ChartContainer
          margin={{
            left: typeof window !== 'undefined' && window.innerWidth < 640 ? 50 : 50,
            right: typeof window !== 'undefined' && window.innerWidth < 640 ? 80 : 120,
            top: 20,
            bottom: 20,
          }}
          xAxis={[
            {
              domainLimit: (min, max) => {
                return { min, max: Math.floor(max + (max * 15) / 100) };
              },
              valueFormatter: (value: number) => formatMoney(value, 0),
              tickLabelStyle: {
                fontSize: 11,
                fontWeight: 'bold',
                fill: '#64748B',
                fontFamily: 'Inter',
              },
            },
          ]}
          yAxis={[
            {
              scaleType: 'band',
              dataKey: 'customerName',
              width: 180,
              tickLabelStyle: {
                fontSize: 11,
                fontWeight: 'bold',
                fill: '#64748B',
                textAnchor: 'end' as const,
                fontFamily: 'Inter',
              },
              tickLabelPlacement: 'middle',
              tickPlacement: 'middle',
            },
          ]}
          dataset={dataSet}
          series={[
            {
              type: 'bar',
              id: 'base',
              dataKey: 'totalAmountInTransactions',
              layout: 'horizontal',
              color: COLORS.PRIMARY_500,
              valueFormatter: (value: number | null) => {
                return `${formatMoney(value ? value : 0, 2)}`;
              },
            },
          ]}
          height={Math.max(400, dataSet.length * 50 + 100)}>
          <BarPlot
            borderRadius={6}
            barLabel={(item) => {
              return `${formatMoney(item.value ? item.value : 0, 2)}`;
            }}
            slots={{ barLabel: BarLabel }}
            slotProps={{
              barLabel: {
                style: {
                  fontSize: '12px',
                  fontWeight: 500,
                  fill: '#1E293B',
                  fontFamily: 'Inter',
                },
              },
            }}
          />
          <ChartsTooltip
            labelFormatter={(label: any) => {
              const item = dataSet?.find((d) => d.customerName === label);
              return item ? item.cleanName : label;
            }}
            valueFormatter={(value: any, context: any) => {
              const item = dataSet?.[context.dataIndex];
              if (item) {
                return [
                  `Revenue: ${formatMoney(value as number, 2)}`,
                  `Market Share: ${item.percentage.toFixed(1)}%`,
                  `Rank: #${item.rank}`,
                ].join('\n');
              }
              return `${formatMoney(value as number, 2)}`;
            }}
            slotProps={{
              root: {
                style: {
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  padding: '12px',
                  minWidth: '200px',
                },
              },
            }}
          />
          <ChartsXAxis />
          <ChartsYAxis />
        </ChartContainer>
      )}
    </div>
  );
}

export default SupplierTopPayingCustomersChart;
